import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../api/axios';

const fieldTypes = [
  { value: 'text', label: 'Текст' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Телефон' },
  { value: 'textarea', label: 'Многострочный текст' },
  { value: 'select', label: 'Выпадающий список' },
  { value: 'number', label: 'Число' },
  { value: 'date', label: 'Дата' },
];

const FormFieldsManager = () => {
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    label: '',
    fieldType: 'text',
    name: '',
    required: false,
    options: '',
    placeholder: '',
    order: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/form-fields', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFields(response.data);
    } catch (error) {
      console.error('Error fetching form fields:', error);
      setError('Ошибка загрузки полей формы');
    }
  };

  const handleOpen = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        label: field.label,
        fieldType: field.fieldType,
        name: field.name,
        required: field.required,
        options: field.options ? field.options.join('\n') : '',
        placeholder: field.placeholder || '',
        order: field.order,
        isActive: field.isActive,
      });
    } else {
      setEditingField(null);
      setFormData({
        label: '',
        fieldType: 'text',
        name: '',
        required: false,
        options: '',
        placeholder: '',
        order: 0,
        isActive: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingField(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        options: formData.fieldType === 'select' && formData.options
          ? formData.options.split('\n').filter(opt => opt.trim())
          : [],
      };

      if (editingField) {
        await axios.put(`/form-fields/${editingField._id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Поле успешно обновлено');
      } else {
        await axios.post('/form-fields', dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Поле успешно создано');
      }

      fetchFields();
      handleClose();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving form field:', error);
      setError(error.response?.data?.message || 'Ошибка при сохранении поля');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить это поле?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/form-fields/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Поле успешно удалено');
      fetchFields();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting form field:', error);
      setError('Ошибка при удалении поля');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Управление полями формы
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Добавить поле
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Порядок</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Имя поля</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Обязательное</TableCell>
              <TableCell>Активно</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field) => (
              <TableRow key={field._id}>
                <TableCell>{field.order}</TableCell>
                <TableCell>{field.label}</TableCell>
                <TableCell>
                  <Chip label={field.name} size="small" />
                </TableCell>
                <TableCell>
                  {fieldTypes.find(t => t.value === field.fieldType)?.label}
                </TableCell>
                <TableCell>
                  <Chip
                    label={field.required ? 'Да' : 'Нет'}
                    color={field.required ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={field.isActive ? 'Да' : 'Нет'}
                    color={field.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpen(field)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(field._id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingField ? 'Редактировать поле' : 'Создать новое поле'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Название поля (для пользователя)"
              name="label"
              value={formData.label}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Имя поля (техническое)"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              helperText="Только латинские буквы, цифры и подчеркивания (например: child_age)"
            />
            <FormControl fullWidth>
              <InputLabel>Тип поля</InputLabel>
              <Select
                name="fieldType"
                value={formData.fieldType}
                onChange={handleChange}
                label="Тип поля"
              >
                {fieldTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Placeholder"
              name="placeholder"
              value={formData.placeholder}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Порядок"
              name="order"
              type="number"
              value={formData.order}
              onChange={handleChange}
              fullWidth
            />
            {formData.fieldType === 'select' && (
              <TextField
                label="Опции (каждая с новой строки)"
                name="options"
                value={formData.options}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
                helperText="Введите каждую опцию с новой строки"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  name="required"
                  checked={formData.required}
                  onChange={handleChange}
                />
              }
              label="Обязательное поле"
            />
            <FormControlLabel
              control={
                <Switch
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
              }
              label="Активно"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.label || !formData.name}>
            {editingField ? 'Обновить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormFieldsManager;
